import { ChangeDetectorRef, Component, HostBinding, Inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, Select, ofActionDispatched } from '@ngxs/store';
import { PublishContentNodesSuccess } from 'app/library/content-node/actions';
import { ContentNodeService } from 'app/library/content-node/services/content-node.service';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { FUSE_ASYNC_DRAWER } from 'app/projects/fuse/src/lib/services/async-drawer.token';
import { UserState } from 'app/projects/user/src/lib/models/user.state';
import { Observable } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { User } from 'talkjs/all';
import { AsyncJobApiService } from '../../../services/async-job.api-service';
import { JobMessageStatus } from './enums/job-message-status.enum';
import { JobStatus } from './enums/job-status.enum';
import { Job } from './models/job.model';
import { Message } from './models/message.model';
import { JobDetailsDialogComponent } from './dialogs/job-details-dialog/job-details-dialog.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ImportUsersDetailsDialogComponent } from './dialogs/import-user-details-dialog/import-user-details-dialog.component';

@Component({
    selector: 'app-async-job-drawer',
    templateUrl: './async-job-drawer.component.html',
    styleUrls: ['./async-job-drawer.component.scss'],
})
export class AsyncJobDrawerComponent extends UnsubscribeOnDestroy implements OnInit, OnDestroy {
    @HostBinding('class.no-jobs')
    isHidden = true;

    @Select(UserState.getMyInfo)
    profile$: Observable<User>;
    profile: User;

    jobDetailsDialogRef: MatDialogRef<JobDetailsDialogComponent>;
    importUsersDetailsDialogRef: MatDialogRef<ImportUsersDetailsDialogComponent>;

    isAsyncJobVisable = false;
    asyncJobs: Job[] = [];
    jobStatus: Job = null;
    jobMessages: Message[];
    jobMessageStatus: string = null;
    jobMessagesVisable = true;
    JobStatus = JobStatus;
    isExpanded = true;
    currentJob: Job;

    get isPanelVisible(): boolean {
        const value = this.asyncJobs.length;

        this.isHidden = !value;

        return !this.isHidden;
    }

    constructor(
        public contentNodeService: ContentNodeService,
        @Inject(FUSE_ASYNC_DRAWER) public asyncJobService: AsyncJobApiService,
        private _action$: Actions,
        private _changeDetection: ChangeDetectorRef,
        public router: Router,
        private _matDialog: MatDialog
    ) {
        super();
    }

    async ngOnInit(): Promise<void> {
        this.asyncJobService.jobStatus.pipe(takeUntil(this._unsubscribeAll)).subscribe(async (job: Job) => {
            if (job) {
                const foundIndex = this.asyncJobs.findIndex((x) => x.id === job.id);
                this.asyncJobs[foundIndex] = this.getFilteredStats(job);
            }
        });

        this.asyncJobService.asyncJobs$.pipe(takeUntil(this._unsubscribeAll)).subscribe((jobs: Job[]) => {
            if (jobs.length > 0) {
                const processedJobs = jobs.map((job) => {
                    return this.getFilteredStats(job); // Call the function to get filtered stats
                });

                if (this.asyncJobService.jobsPage > 1) {
                    this.asyncJobs = this.asyncJobs.concat(processedJobs);
                } else {
                    this.asyncJobs = processedJobs;
                }

                const isDrawerOpen: boolean = Boolean(localStorage.getItem('isDrawerOpen'));

                // Initialize infinite scroll only once after results
                if (this.asyncJobService.jobsPage === 1 && isDrawerOpen) {
                    setTimeout(() => {
                        this.initInfiniteScroll();
                    });
                }

                // If there are no changes, clean asyncJobs to hide drawer
                if (!isDrawerOpen) {
                    localStorage.removeItem('isDrawerOpen');
                    this.asyncJobs = [];
                }

                this._changeDetection.detectChanges();
            }
        });

        this.profile$
            .pipe(
                filter((profile) => !!profile),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((profile) => {
                this.profile = profile;
            });
    }

    getFilteredStats(job: Job): Job {
        // Extract total and filtered stats
        const statsArray = Object.entries(job.stats)
            .filter(([_, value]) => value !== 0 || job.communicatedStatus === JobStatus.Created || job.communicatedStatus === JobStatus.InProgress) // Remove keys with 0 values
            .map(([key, value]) => ({ key, value }));
        const totalStat = statsArray.find((stat) => stat.key === 'total')?.value || null;
        const filteredStats = statsArray.filter((stat) => stat.key !== 'total');

        // Return job with new properties
        return {
            ...job,
            totalStat, // Store total separately
            filteredStats, // Store filtered stats separately
        };
    }

    navigateToModule(moduleId: string) {
        this.router.navigate([`modules/${moduleId}/overview`]);
    }

    private initInfiniteScroll(): void {
        const expansionPanel = document.getElementsByClassName('mat-expansion-panel-content')[0];
        expansionPanel.addEventListener('scroll', (e) => {
            const scrollTop = (e.target as HTMLElement).scrollTop;
            const clientHeight = (e.target as HTMLElement).clientHeight;
            const scrollHeight = (e.target as HTMLElement).scrollHeight;
            const hiddenHeight = scrollHeight - clientHeight;

            if (scrollTop === hiddenHeight && this.asyncJobService.jobsPage < this.asyncJobService.jobsPagination.numberOfPages) {
                this.asyncJobService.getAllJobs(true, ++this.asyncJobService.jobsPage);
            }
        });
    }

    async onGetJobStatus(job: Job): Promise<void> {
        if (this.jobStatus) {
            if (!this.jobStatus.isRead) {
                const isRead = await this.asyncJobService.markJobAsRead(job.id);
                if (isRead) {
                    job.isRead = true;
                }
            }
            this.onGetJobMessages(job.id);

            if (this.jobStatus.communicatedStatus === JobStatus.InProgress || this.jobStatus.communicatedStatus === JobStatus.Created) {
                const intervalId = setInterval(async () => {
                    this.jobStatus = await this.asyncJobService.getJobStatus(job.id);
                    this.onGetJobMessages(job.id);
                    if (
                        this.jobStatus.communicatedStatus === JobStatus.Finished ||
                        this.jobStatus.communicatedStatus === JobStatus.TimedOut ||
                        this.jobStatus.communicatedStatus === JobStatus.Canceled
                    ) {
                        clearInterval(intervalId);
                    }
                }, 3000);
            }
        }
    }

    async openJobDetailsDialog(job: Job): Promise<void> {
        this.currentJob = job;

        this.jobDetailsDialogRef = this._matDialog.open(JobDetailsDialogComponent, {
            width: '848px',
            disableClose: false,
        });

        this.jobDetailsDialogRef.componentInstance.title = 'ASYNC_DRAWER.JOB_TYPE.' + job.type;
        this.jobDetailsDialogRef.componentInstance.totalCount = `(${job.stats.total})`;
        this.jobDetailsDialogRef.componentInstance.job = this.currentJob;

        this.jobDetailsDialogRef.afterClosed().subscribe(() => {
            this.asyncJobService.jobStatus.next(null);

            this.jobDetailsDialogRef = null;
        });
    }

    async onGetJobMessages(jobId: string, page = 1, pageSize = 5, messageStatus?: string, numberOfMessages?: number): Promise<void> {
        if (messageStatus) {
            if (numberOfMessages > 0) {
                if (messageStatus !== this.jobMessageStatus) {
                    await this.onGetJobMessagesByStatus(jobId, page, pageSize, messageStatus);
                } else if (messageStatus === this.jobMessageStatus) {
                    this.jobMessagesVisable = !this.jobMessagesVisable;
                }
            } else if (page > 1) {
                await this.onGetJobMessagesByStatus(jobId, page, pageSize, messageStatus);
            } else {
                this.jobMessages = [];
                this.jobMessageStatus = null;
            }
        } else {
            if (this.jobStatus.stats.failed > 0) {
                await this.onGetJobMessagesByStatus(jobId, page, pageSize, JobMessageStatus.Failed);
            } else if (this.jobStatus.stats.pending > 0) {
                await this.onGetJobMessagesByStatus(jobId, page, pageSize, JobMessageStatus.Pending);
            } else {
                await this.onGetJobMessagesByStatus(jobId, page, pageSize, JobMessageStatus.Succeeded);
            }
        }
    }

    async onGetJobMessagesByStatus(jobId: string, page: number, pageSize: number, messageStatus: string): Promise<void> {
        this.jobMessagesVisable = true;
        const jobMessages = await this.asyncJobService.getJobMessages(jobId, page, pageSize, messageStatus);
        if (jobMessages) {
            this.jobMessageStatus = messageStatus;
            if (page > 1) {
                this.jobMessages = this.jobMessages.concat(jobMessages);
            } else {
                this.jobMessages = jobMessages;
            }
        }
    }

    viewReport(job: Job): void {
        this.importUsersDetailsDialogRef = this._matDialog.open(ImportUsersDetailsDialogComponent, {
            width: '848px',
            disableClose: false,
        });

        this.importUsersDetailsDialogRef.componentInstance.title = 'GENERAL.BUTTON.REPORT';
        this.importUsersDetailsDialogRef.componentInstance.jobId = job.id;

        this.importUsersDetailsDialogRef.afterClosed().subscribe(() => {
            this.importUsersDetailsDialogRef = null;
        });
    }

    onToggleAsyncJob(): void {
        this.jobMessages = [];
        this.jobMessageStatus = null;
        this.isAsyncJobVisable = !this.isAsyncJobVisable;
    }

    async onCancelAsyncJob(job: Job): Promise<void> {
        const jobCanceled = await this.asyncJobService.cancelJob(job);
        if (jobCanceled) {
            const foundIndex = this.asyncJobs.findIndex((x) => x.id === jobCanceled.id);
            this.asyncJobs[foundIndex] = jobCanceled;
        }
    }

    async onCloseAsyncJob(job: Job): Promise<void> {
        const jobClosed = await this.asyncJobService.closeJob(job);
        if (jobClosed) {
            this.asyncJobs = this.asyncJobs.filter((asyncJob) => asyncJob.id !== jobClosed.id);
            this.asyncJobService.completedJobs--;
            this.asyncJobService.jobsPagination.numberOfResults--;
            this.asyncJobService.jobsPaginationChanged.next(true);
            if (this.asyncJobs.length === 0) {
                localStorage.removeItem('isDrawerOpen');
            }
        }
    }

    onCloseDrawer(): void {
        localStorage.removeItem('isDrawerOpen');
        this.asyncJobs = [];
        this._changeDetection.detectChanges();
    }

    onExpandedChange(value: boolean): void {
        this.isExpanded = !this.isExpanded;
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
    }
}
